import { ethers } from 'ethers';
import { CHAINS, CONTRACTS, STORAGE_KEYS } from '../config/constants';

// Transaction status constants
export const TRANSACTION_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  FAILED: 'failed',
  EXPIRED: 'expired'
};

// Transaction manager for handling direct contract interactions
export class TransactionManager {
  MAX_TRANSACTIONS = 100;
  constructor() {
    this.transactions = this.loadTransactions();
    this.eventListeners = new Map();
  }

  // Load transactions from local storage
  loadTransactions() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.RECENT_TRANSACTIONS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load transactions from storage:', error);
      return [];
    }
  }

  // Save transactions to local storage
  saveTransactions() {
    try {
      localStorage.setItem(STORAGE_KEYS.RECENT_TRANSACTIONS, JSON.stringify(this.transactions));
    } catch (error) {
      console.error('Failed to save transactions to storage:', error);
    }
  }

  // Add a new transaction
  addTransaction(transaction) {
    const newTransaction = {
      id: transaction.txHash,
      txHash: transaction.txHash,
      fromChain: transaction.fromChain,
      toChain: transaction.toChain,
      amount: transaction.amount,
      recipient: transaction.recipient,
      status: TRANSACTION_STATUS.PENDING,
      timestamp: Date.now(),
      events: [],
      ...transaction
    };

    // Add to beginning of array (most recent first)
    this.transactions.unshift(newTransaction);

    // Keep only last MAX_TRANSACTIONS transactions
    if (this.transactions.length > this.MAX_TRANSACTIONS) {
      this.transactions = this.transactions.slice(0, this.MAX_TRANSACTIONS);
    }

    this.saveTransactions();
    return newTransaction;
  }

  // Update transaction status
  updateTransaction(txHash, updates) {
    const index = this.transactions.findIndex(tx => tx.txHash === txHash);
    if (index !== -1) {
      this.transactions[index] = {
        ...this.transactions[index],
        ...updates,
        lastUpdated: Date.now()
      };
      this.saveTransactions();
      return this.transactions[index];
    }
    return null;
  }

  // Get transaction by hash
  getTransaction(txHash) {
    return this.transactions.find(tx => tx.txHash === txHash);
  }

  // Get transactions for a specific address
  getTransactionsForAddress(address) {
    return this.transactions.filter(tx => 
      tx.recipient?.toLowerCase() === address?.toLowerCase() ||
      tx.sender?.toLowerCase() === address?.toLowerCase()
    );
  }

  // Get pending transactions
  getPendingTransactions() {
    return this.transactions.filter(tx => 
      tx.status === TRANSACTION_STATUS.PENDING || 
      tx.status === TRANSACTION_STATUS.CONFIRMED
    );
  }

  // Track transaction status via blockchain events
  async trackTransaction(txHash, fromChain, toChain, provider) {
    try {
      const transaction = this.getTransaction(txHash);
      if (!transaction) return;

      // Wait for transaction confirmation
      const receipt = await provider.waitForTransaction(txHash, 1);
      
      if (receipt.status === 1) {
        this.updateTransaction(txHash, {
          status: TRANSACTION_STATUS.CONFIRMED,
          blockNumber: receipt.blockNumber,
          gasUsed: receipt.gasUsed.toString(),
          confirmations: 1
        });

        // Parse events from the receipt
        await this.parseTransactionEvents(txHash, receipt, fromChain);

        // Start monitoring for completion on destination chain
        this.monitorDestinationChain(txHash, toChain);
      } else {
        this.updateTransaction(txHash, {
          status: TRANSACTION_STATUS.FAILED,
          error: 'Transaction failed'
        });
      }
    } catch (error) {
      console.error('Failed to track transaction:', error);
      this.updateTransaction(txHash, {
        status: TRANSACTION_STATUS.FAILED,
        error: error.message
      });
    }
  }

  // Parse events from transaction receipt
  async parseTransactionEvents(txHash, receipt, chainId) {
    try {
      const transaction = this.getTransaction(txHash);
      if (!transaction) return;

      // PuffBridge uses unified ABI for both chains
      const { default: PuffBridgeABI } = await import('../abi/PuffBridge.json');
      const iface = new ethers.utils.Interface(PuffBridgeABI);
      const events = [];

      // Parse all logs in the receipt
      for (const log of receipt.logs) {
        try {
          const parsed = iface.parseLog(log);
          events.push({
            name: parsed.name,
            args: parsed.args,
            address: log.address,
            blockNumber: log.blockNumber,
            transactionHash: log.transactionHash
          });
        } catch (error) {
          // Log might not be from our contract, skip it
        }
      }

      // Update transaction with parsed events
      this.updateTransaction(txHash, {
        events: events,
        sourceChainConfirmed: true
      });

      // Check for PuffBridge specific events
      const bridgeEvent = events.find(e => 
        e.name === 'BridgeRequestCreated'
      );

      if (bridgeEvent) {
        const srcNonce = bridgeEvent.args.srcNonce ? bridgeEvent.args.srcNonce.toString() : null;
        this.updateTransaction(txHash, {
          srcNonce: srcNonce,
          sourceEventConfirmed: true
        });
      }

    } catch (error) {
      console.error('Failed to parse transaction events:', error);
    }
  }

  // Generate bridge ID from event data (fallback) - not needed for PuffBridge
  generateBridgeId(event) {
    // PuffBridge uses srcNonce for tracking, not bridgeId
    return event.args.srcNonce ? event.args.srcNonce.toString() : null;
  }

  // Monitor destination chain for completion
  async monitorDestinationChain(txHash, toChain) {
    try {
      const transaction = this.getTransaction(txHash);
      if (!transaction || !transaction.srcNonce) {
        // Retry after 30 seconds if srcNonce not yet available
        setTimeout(() => this.monitorDestinationChain(txHash, toChain), 30000);
        return;
      }

      // Get destination chain provider
      const destProvider = new ethers.providers.JsonRpcProvider(
        toChain === CHAINS.RONIN.id ? CHAINS.RONIN.rpcUrl : CHAINS.BNB.rpcUrl
      );

      // Get destination bridge contract
      const destBridgeAddress = toChain === CHAINS.RONIN.id 
        ? CONTRACTS.RONIN.BRIDGE 
        : CONTRACTS.BNB.BRIDGE;

      // PuffBridge uses unified ABI for both chains
      const { default: PuffBridgeABI } = await import('../abi/PuffBridge.json');
      const destBridge = new ethers.Contract(destBridgeAddress, PuffBridgeABI, destProvider);

      // Listen for BridgeReceiveExecuted events
      // Event signature: BridgeReceiveExecuted(bytes32 indexed receiveHash, uint64 indexed srcNonce, address indexed receiver, uint128 amount)
      const filter = destBridge.filters.BridgeReceiveExecuted(
        null, // receiveHash - let it be null to catch all
        transaction.srcNonce, // srcNonce
        transaction.recipient // receiver
      );

      // Set up event listener
      const listener = (receiveHash, srcNonce, receiver, amount, event) => {
        // Convert BigInt to string for comparison
        const eventSrcNonce = srcNonce.toString();
        const txSrcNonce = transaction.srcNonce.toString();
        
        if (eventSrcNonce === txSrcNonce && receiver.toLowerCase() === transaction.recipient.toLowerCase()) {
          this.updateTransaction(txHash, {
            status: TRANSACTION_STATUS.COMPLETED,
            completedAt: Date.now(),
            destinationTxHash: event.transactionHash,
            destinationBlockNumber: event.blockNumber
          });

          // Remove listener
          destBridge.off(filter, listener);
          this.eventListeners.delete(txHash);
        }
      };

      // Check for past events first (in case transaction was already completed)
      try {
        const currentBlock = await destProvider.getBlockNumber();
        const fromBlock = Math.max(0, currentBlock - 1000); // Check last 1000 blocks
        
        const pastEvents = await destBridge.queryFilter(filter, fromBlock, currentBlock);
        
        for (const event of pastEvents) {
          const eventSrcNonce = event.args.srcNonce.toString();
          const txSrcNonce = transaction.srcNonce.toString();
          
          if (eventSrcNonce === txSrcNonce && event.args.receiver.toLowerCase() === transaction.recipient.toLowerCase()) {
            this.updateTransaction(txHash, {
              status: TRANSACTION_STATUS.COMPLETED,
              completedAt: Date.now(),
              destinationTxHash: event.transactionHash,
              destinationBlockNumber: event.blockNumber
            });
            return; // Don't set up future listener if already completed
          }
        }
      } catch (error) {
        console.error(`Error checking past events for ${txHash}:`, error);
      }

      // Set up listener for future events
      destBridge.on(filter, listener);
      this.eventListeners.set(txHash, { provider: destProvider, bridge: destBridge, filter, listener });

      // Set timeout for monitoring (24 hours)
      setTimeout(() => {
        if (this.eventListeners.has(txHash)) {
          const tx = this.getTransaction(txHash);
          if (tx && tx.status !== TRANSACTION_STATUS.COMPLETED) {
            this.updateTransaction(txHash, {
              status: TRANSACTION_STATUS.EXPIRED,
              error: 'Transaction monitoring timeout'
            });
          }
          
          // Cleanup listener
          destBridge.off(filter, listener);
          this.eventListeners.delete(txHash);
        }
      }, 24 * 60 * 60 * 1000); // 24 hours

    } catch (error) {
      console.error('Failed to monitor destination chain:', error);
    }
  }

  // Cleanup all event listeners
  cleanup() {
    for (const [txHash, { bridge, filter, listener }] of this.eventListeners) {
      bridge.off(filter, listener);
    }
    this.eventListeners.clear();
  }

  // Get transaction status summary
  getStatusSummary() {
    const total = this.transactions.length;
    const pending = this.transactions.filter(tx => tx.status === TRANSACTION_STATUS.PENDING).length;
    const confirmed = this.transactions.filter(tx => tx.status === TRANSACTION_STATUS.CONFIRMED).length;
    const completed = this.transactions.filter(tx => tx.status === TRANSACTION_STATUS.COMPLETED).length;
    const failed = this.transactions.filter(tx => tx.status === TRANSACTION_STATUS.FAILED).length;

    return {
      total,
      pending,
      confirmed,
      completed,
      failed,
      success_rate: total > 0 ? ((completed / total) * 100).toFixed(1) : '0.0'
    };
  }
}

// Create singleton instance
export const transactionManager = new TransactionManager();

// Export utility functions
export const addTransaction = (transaction) => transactionManager.addTransaction(transaction);
export const updateTransaction = (txHash, updates) => transactionManager.updateTransaction(txHash, updates);
export const getTransaction = (txHash) => transactionManager.getTransaction(txHash);
export const getTransactionsForAddress = (address) => transactionManager.getTransactionsForAddress(address);
export const getPendingTransactions = () => transactionManager.getPendingTransactions();
export const trackTransaction = (txHash, fromChain, toChain, provider) => 
  transactionManager.trackTransaction(txHash, fromChain, toChain, provider);

export default transactionManager;